import { useState, useEffect } from 'react';

import { Trans } from '@lingui/react/macro';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie } from 'lucide-react';

import { Button } from '~/components/ui/button';
import { Switch } from '~/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  social: boolean;
  advertising: boolean;
}

// Custom event name for opening cookie settings
export const OPEN_COOKIE_SETTINGS_EVENT = 'openCookieSettings';

// Helper function to open cookie settings from anywhere
export function openCookieSettings() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
  }
}

export default function CookieNotice() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always enabled
    analytics: false,
    social: false,
    advertising: false,
  });

  useEffect(() => {
    // Only show cookie notice on speak.pavone.ai (European users)
    if (typeof window !== 'undefined' && !window.location.hostname.includes('speak.pavone.ai')) {
      return;
    }

    // Check if user has already accepted/rejected cookies
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show notice after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const savedPrefs = JSON.parse(localStorage.getItem('cookiePreferences') || '{}');
        setPreferences(prev => ({ ...prev, ...savedPrefs }));
      } catch {
        // Ignore parse errors
      }
    }
  }, []);

  const handleAccept = () => {
    const allEnabled: CookiePreferences = {
      essential: true,
      analytics: true,
      social: true,
      advertising: true,
    };
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookiePreferences', JSON.stringify(allEnabled));
    setPreferences(allEnabled);
    setIsVisible(false);
  };

  const handleDecline = () => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      analytics: false,
      social: false,
      advertising: false,
    };
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookiePreferences', JSON.stringify(essentialOnly));
    setPreferences(essentialOnly);
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', 'customized');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    setShowSettings(false);
    setIsVisible(false);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'essential') return; // Can't toggle essential
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Listen for custom event to open settings from external components (e.g., Footer)
  useEffect(() => {
    const handleOpenSettings = () => {
      setShowSettings(true);
    };

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
    return () => {
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
    };
  }, []);

  return (
    <>
      <AnimatePresence>
        {isVisible && !showSettings && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto z-50"
          >
            <div className="w-full md:w-[400px] lg:w-[440px]">
              <motion.div
                className="bg-white rounded-2xl shadow-2xl p-4 md:p-6 relative overflow-hidden border border-brand-gold/20"
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1 }}
              >
                {/* Header with cookie emoji */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">🍪</span>
                  <h3 className="text-base md:text-lg font-semibold">
                    <Trans>Cookie Preferences</Trans>
                  </h3>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  <Trans>
                    We use cookies to ensure the best experience, understand how the site is used, and support basic functionality. You can choose to accept all cookies or adjust your settings.
                  </Trans>
                </p>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => setShowSettings(true)}
                    variant="outline"
                    className="flex-1 border-brand-dark-blue/30 text-brand-dark-blue hover:bg-brand-dark-blue/5"
                  >
                    <Trans>Settings</Trans>
                  </Button>
                  <Button
                    onClick={handleDecline}
                    variant="outline"
                    className="flex-1 border-brand-dark-blue/30 text-brand-dark-blue hover:bg-brand-dark-blue/5"
                  >
                    <Trans>Decline</Trans>
                  </Button>
                  <Button
                    onClick={handleAccept}
                    className="flex-1 bg-brand-gold hover:bg-brand-gold/90 text-white"
                  >
                    <Trans>Accept</Trans>
                  </Button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-white max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-zinc-900">
              <Trans>Cookie Preferences</Trans>
            </DialogTitle>
            <p className="text-sm text-zinc-500 mt-1">
              <Trans>Manage your cookie preferences below.</Trans>
            </p>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Essential */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="font-medium text-zinc-900">
                  <Trans>Essential</Trans>
                </h4>
                <p className="text-sm text-zinc-500">
                  <Trans>Required for the website to function properly</Trans>
                </p>
                <p className="text-xs text-zinc-400 mt-1">
                  <Trans>Status: Always enabled</Trans>
                </p>
              </div>
              <div className="flex items-center">
                <span className="text-sm text-zinc-500 bg-zinc-100 px-3 py-1 rounded-full">
                  <Trans>Always On</Trans>
                </span>
              </div>
            </div>

            {/* Analytics */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="font-medium text-zinc-900">
                  <Trans>Analytics</Trans>
                </h4>
                <p className="text-sm text-zinc-500">
                  <Trans>Help us understand how visitors interact with our website</Trans>
                </p>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={() => togglePreference('analytics')}
              />
            </div>

            {/* Social */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="font-medium text-zinc-900">
                  <Trans>Social</Trans>
                </h4>
                <p className="text-sm text-zinc-500">
                  <Trans>Enable social media features and sharing</Trans>
                </p>
              </div>
              <Switch
                checked={preferences.social}
                onCheckedChange={() => togglePreference('social')}
              />
            </div>

            {/* Advertising */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="font-medium text-zinc-900">
                  <Trans>Advertising</Trans>
                </h4>
                <p className="text-sm text-zinc-500">
                  <Trans>Personalize advertisements and measure their performance</Trans>
                </p>
              </div>
              <Switch
                checked={preferences.advertising}
                onCheckedChange={() => togglePreference('advertising')}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6">
            <Button
              onClick={() => setShowSettings(false)}
              variant="outline"
              className="flex-1 border-brand-dark-blue/30 text-brand-dark-blue hover:bg-brand-dark-blue/5"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              onClick={handleSavePreferences}
              className="flex-1 bg-brand-gold hover:bg-brand-gold/90 text-white"
            >
              <Trans>Save Preferences</Trans>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
